// paex-store-chip.js — the site-wide "My store" chip v2 (Store Availability
// UX design 1i). Requires paex-store-core.js first. Four states: none / open /
// closed / out-of-footprint (absent, fail closed). A set store opens the
// compact dropdown; no store → the summary click opens the full selector
// directly. Both chip instances resolve independently and stay in sync with
// the PDP block through the paex:store-change event + cookie.

if (!customElements.get('paex-store-chip')) {
  customElements.define(
    'paex-store-chip',
    class PaexStoreChip extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();

        const embed = this.querySelector('script[data-paex-chip-data]') || document.querySelector('script[data-paex-chip-data]');   // T6: one island per page
        try {
          this.data = embed ? JSON.parse(embed.textContent) : null;
        } catch (error) {
          this.data = null;
        }
        if (!this.data) return;
        // fresh design 3a: a store past its closing_date is dropped from
        // every surface on the DATE (warehouse tz), not on the active flag
        this.data.stores = paexStorePruneExpired(this.data.stores);
        this.displayNames = paexDeliveryDisplayNames(this.data.stores);

        this.storeId = null;
        this.storeIsExplicit = false;
        this.radiusKm = Number(this.dataset.radius) || 75;
        this.forceFootprint = this.hasAttribute('data-force-footprint'); // editor/QA
        // host-interface contract for the shared <paex-store-drawer>: no product
        // context on non-PDP pages, so no stock words in the drawer rows
        this.variantId = null;

        document.addEventListener(
          PAEX_STORE_EVENT,
          (event) => {
            if (!event.detail || !event.detail.storeId) return;
            this.setStore(String(event.detail.storeId), true);
          },
          { signal: this.abortController.signal }
        );

        // Postal-code safety net (Sasha 2026-10-12): a typed destination
        // inside the radius shows the chip even when IP geo said no
        document.addEventListener(
          PAEX_DEST_EVENT,
          (event) => this.applyDest(event.detail),
          { signal: this.abortController.signal }
        );

        this.bindDisclosure();

        window.addEventListener(
          PAEX_RCC_STORE_SET_EVENT,
          (event) => {
            if (!event.detail || !event.detail.placeId) return;
            this.setStore(String(event.detail.placeId), true);
          },
          { signal: this.abortController.signal }
        );

        window.addEventListener(
          PAEX_RCC_STORE_REMOVED_EVENT,
          () => {
            this.storeId = null;
            this.storeIsExplicit = false;
            if (!window.Shopify?.designMode) webvista.setCookie(PAEX_STORE_COOKIE, '', -1);
            this.render();
            if (this.dataset.context === 'header') this.openDrawer();
          },
          { signal: this.abortController.signal }
        );

        // every [data-chip-open] opens the selector: the band's strip body + its "Change"
        // text button (CHANGES 4) — the band has no <details> dropdown at all
        this.querySelectorAll('[data-chip-open]').forEach((button) => {
          button.addEventListener('click', () => this.openDrawer(), {
            signal: this.abortController.signal,
          });
        });

        this.resolveStore();
        this.render();
        // hours flip open→closed while the page sits idle; keep the dot honest
        this.hoursTimer = setInterval(() => this.render(), 60000);
      }

      disconnectedCallback() {
        this.abortController.abort();
        if (this.hoursTimer) clearInterval(this.hoursTimer);
      }

      /* ---------------- disclosure (compact dropdown, design 1i-E) ---------------- */

      bindDisclosure() {
        const signal = this.abortController.signal;
        const details = this.querySelector('[data-chip-details]');
        const summary = this.querySelector('[data-chip-summary]');
        if (!details || !summary) return;

        summary.addEventListener(
          'click',
          (event) => {
            // No store yet: the chip's one action is the selector (design 1i-A).
            if (!this.storeId) {
              event.preventDefault();
              this.openDrawer();
            }
          },
          { signal }
        );

        details.addEventListener(
          'toggle',
          () => {
            summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
            // 1i-E: the open strip shows the "My store:" label again —
            // just the label; the Select-a-store action stays hidden
            const labelRow = this.querySelector('.paex-store-chip__label');
            const selectAction = this.querySelector('[data-chip-store]');
            if (labelRow && this.storeId) {
              labelRow.hidden = !details.open;
              if (selectAction) selectAction.hidden = true;
            }
            if (details.open) this.renderPanel();
          },
          { signal }
        );

        // DropMenu-style closers: focus-out, outside click and Escape.
        // WebKit hardening (2026-08-01): Safari never focuses a clicked
        // <button>/<a>, so pressing a panel control blurred the summary to
        // <body> and the deferred focusout close hid the panel BETWEEN
        // mousedown and mouseup — the control's click landed on <body> and
        // "Change store" never opened the selector (Safari-only; Chrome
        // focuses the button, keeping focus inside the panel). A press that
        // starts inside the panel therefore never closes it via focusout;
        // real outside interactions close through the document click closer
        // below, and Tab-out (no pointer involved) still closes here.
        let pressInside = false;
        const releasePress = () => {
          // cleared a tick AFTER release so the focusout check scheduled at
          // mousedown still sees the press, whatever the frame timing was
          setTimeout(() => { pressInside = false; }, 0);
        };
        details.addEventListener('pointerdown', () => { pressInside = true; }, { signal });
        document.addEventListener('pointerup', releasePress, { signal });
        document.addEventListener('pointercancel', releasePress, { signal });

        details.addEventListener(
          'focusout',
          (event) => {
            if (event.relatedTarget && details.contains(event.relatedTarget)) return;
            requestAnimationFrame(() => {
              if (pressInside) return; // let the in-panel click land and act
              if (details.open && !details.contains(document.activeElement)) {
                details.removeAttribute('open');
              }
            });
          },
          { signal }
        );

        // Outside close that doesn't depend on focus tracking (Safari may
        // never focus the summary at all). Capture phase so a stopPropagation
        // elsewhere can't hide outside clicks; click — not pointerdown — so a
        // touch-scroll in the mobile menu drawer doesn't dismiss the inline
        // accordion.
        document.addEventListener(
          'click',
          (event) => {
            if (!details.open) return;
            if (event.composedPath().includes(details)) return;
            details.removeAttribute('open');
          },
          { signal, capture: true }
        );
        // house components (HeaderDrawer) close on Escape KEYUP — intercept
        // there so one press closes only the chip panel, not the whole menu
        details.addEventListener(
          'keyup',
          (event) => {
            if (event.key === 'Escape' && details.open) {
              event.stopPropagation();
              details.removeAttribute('open');
              summary.focus();
            }
          },
          { signal }
        );
        details.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape' && details.open) event.stopPropagation();
          },
          { signal }
        );

        const change = this.querySelector('[data-panel-change]');
        if (change) {
          change.addEventListener(
            'click',
            () => {
              details.removeAttribute('open');
              this.openDrawer();
            },
            { signal }
          );
        }
      }

      /* ---------------- resolution + footprint (fail closed) ---------------- */

      resolveStore() {
        const fromCookie = webvista.getCookie(PAEX_STORE_COOKIE) || webvista.getCookie(PAEX_RCC_STORE_COOKIE);
        if (fromCookie && paexDeliveryHasStore(this.data.stores, fromCookie)) {
          this.storeId = fromCookie;
          this.storeIsExplicit = true;
          this.setAttribute('data-in-footprint', ''); // a chosen store always wins
          return;
        }
        const preferred = this.dataset.preferredStoreId;
        if (preferred && paexDeliveryHasStore(this.data.stores, preferred)) {
          this.setStore(preferred, true);
          return;
        }
        this.applyDest(paexDestRead()); // safety net: session typed destination

        const geo = paexDeliveryReadGeo();
        if (geo) {
          this.applyGeo(geo);
        }
        // keep listening: the selector's "Use my location" rebroadcasts geo
        document.addEventListener(
          PAEX_GEO_EVENT,
          (event) => this.applyGeo(event.detail),
          { signal: this.abortController.signal }
        );
      }

      setStore(storeId, persist) {
        if (!paexDeliveryHasStore(this.data.stores, storeId)) return;
        this.storeId = storeId;
        this.storeIsExplicit = persist;
        this.setAttribute('data-in-footprint', '');
        if (persist && !window.Shopify?.designMode) {
          webvista.setCookie(PAEX_STORE_COOKIE, storeId, PAEX_STORE_COOKIE_HOURS);
        }
        this.render();
      }

      applyGeo(geo) {
        if (this.storeIsExplicit || !geo) return;
        if (typeof paexDeliveryUsableGeo === 'function' && !paexDeliveryUsableGeo(geo)) return;
        const fit = paexFootprint(geo, this.data.stores, this.radiusKm);
        this.lastGeoFit = fit;
        // safety net: a promoting / pending typed destination keeps the chip
        this.toggleAttribute('data-in-footprint', this.forceFootprint || fit.inFootprint || Boolean(this.destGate || this.destPending));
        // W3.5 CLS: verdict settled — releases the reserved header slot when negative
        this.toggleAttribute('data-paex-gate-settled', true);
        if (fit.inFootprint && fit.nearestId) {
          this.storeId = fit.nearestId; // geo result is NOT persisted
        }
        this.render();
      }

      // Postal-code safety net (Sasha 2026-10-12) — same rule as the
      // availability block (paexDestFootprint): promote only, session memory
      applyDest(dest) {
        if (this.storeIsExplicit || !paexDestResolvable(dest)) return;
        const seq = (this.destSeq = (this.destSeq || 0) + 1);
        this.destPending = true;
        const drawer = document.querySelector('[data-fsa-url]');
        paexDestFootprint(dest, this.data.stores, this.radiusKm, (drawer && drawer.dataset.fsaUrl) || '')
          .catch(() => null)
          .then((fit) => {
            if (seq !== this.destSeq) return;
            this.destPending = false;
            if (!this.isConnected) return;
            if (fit && fit.inFootprint && !this.storeIsExplicit) {
              this.destGate = true;
              this.setAttribute('data-in-footprint', '');
              this.toggleAttribute('data-paex-gate-settled', true);
              if (fit.nearestId && !this.storeId) this.storeId = fit.nearestId; // NOT persisted
              this.render();
              return;
            }
            if (!this.destGate && this.lastGeoFit) {
              this.toggleAttribute('data-in-footprint', this.forceFootprint || this.lastGeoFit.inFootprint);
            }
          });
      }

      /* ---------------- rendering ---------------- */

      render() {
        const texts = this.data.texts;
        const labelRow = this.querySelector('.paex-store-chip__label');
        const nameTarget = this.querySelector('[data-chip-name]');
        const nameLine = this.querySelector('[data-chip-nameline]') || nameTarget; // name + desktop pill row (6.1)
        const hoursRow = this.querySelector('[data-chip-hours]');
        const hoursText = this.querySelector('[data-chip-hours-text]');
        const caret = this.querySelector('[data-chip-caret]');
        const fulfilRow = this.querySelector('[data-chip-fulfil]');
        if (!labelRow || !nameTarget || !hoursRow || !hoursText) return;

        const store =
          this.storeId && paexDeliveryHasStore(this.data.stores, this.storeId)
            ? this.data.stores[this.storeId]
            : null;

        const detailsEl = this.querySelector('[data-chip-details]');
        const panelOpen = Boolean(detailsEl && detailsEl.open);

        // BOPIS (checklist 9 / design 6.1–6.2, 2026-10-23): data-fulfil-store marks a chip whose
        // store is in the program TODAY ("pickup", "local" or "pickup local"); every new look below
        // (search-field sizing, header pill, short mobile hours, weekly hours in the flyout) is
        // gated on it so a non-participating / paused store keeps today's chip byte for byte.
        const participation = this.participation(store);
        if (participation) this.setAttribute('data-fulfil-store', participation);
        else this.removeAttribute('data-fulfil-store');

        if (!store) {
          // state A: "My store: Select a store" (underlined action)
          this.dataset.state = 'none';
          labelRow.hidden = false;
          nameTarget.hidden = true;
          nameLine.hidden = true;
          hoursRow.hidden = true;
          if (caret) caret.hidden = true;
          if (fulfilRow) fulfilRow.hidden = true;
          this.namePill(nameLine, '');
          return;
        }

        // states B/C: name on line 1, dot + open/closed on line 2
        const info = paexStoreHoursInfo(store);
        const open = Boolean(info && info.state !== 'closed');
        this.dataset.state = open ? 'open' : 'closed';
        labelRow.hidden = !panelOpen; // the open strip shows the label (1i-E)
        nameTarget.hidden = false;
        nameLine.hidden = false;
        // fresh design: "header chip shows the glyph beside the store name
        // when the chosen store is a popup" — text only, geometry untouched
        const displayName = this.displayNames[this.storeId] || '';
        const name = (store.type === 'seasonal_popup' ? '🎃 ' : '') + displayName;
        if (nameTarget.textContent !== name) nameTarget.textContent = name;

        let line = '';
        if (info) {
          if (info.state === 'open') {
            // checklist 9 (mobile): the SHORT form "Open · 9 p.m." / « Ouvert · 21 h » so the
            // closing time always shows — on the drawer row for a participating store only; on the
            // phone band (CHANGES 4, one 36px line) for every store the band shows
            const context = this.dataset.context;
            const short = (participation && context === 'menu') || context === 'band' ? this.shortOpenLine(info.closeHour) : '';
            line = short || this.hourLabel(texts.openUntil, 'close', info.closeHour);
          } else if (info.state === 'closes_soon') {
            line = this.hourLabel(texts.closesSoon, 'close', info.closeHour);
          } else if (info.nextOpenHour != null) {
            line = info.nextOpenIsTomorrow
              ? this.hourLabel(texts.opensTomorrow, 'open', info.nextOpenHour)
              : this.hourLabel(texts.opensAt, 'open', info.nextOpenHour);
          }
        }
        hoursText.textContent = line;
        hoursRow.hidden = line === '';
        if (caret) caret.hidden = false;
        // BOPIS pills in the menu-drawer row (both services); the header summary gets ONE
        // "Pickup available" pill right after the store NAME when pickup is offered (6.1),
        // none otherwise (6.2) — desktop only (the CSS shows it from 1200 up)
        if (fulfilRow) {
          if (this.dataset.context === 'menu') this.fulfilPills(fulfilRow, store);
          else fulfilRow.hidden = true;
        }
        // Sasha 2026-10-23: the phone band shows the same pill AFTER the hours ("Québec · Open · 9 p.m. [Pickup
        // available]"), the header summary keeps it right after the store name
        const pillLabel = (this.dataset.context === 'header' || this.dataset.context === 'band') && participation.indexOf('pickup') !== -1 ? this.safeText(texts.chipPickup) : '';
        if (this.dataset.context === 'band') {
          const pillHost = hoursRow && !hoursRow.hidden ? hoursRow : nameLine;
          if (pillHost !== nameLine) this.namePill(nameLine, '');
          if (pillHost !== hoursRow && hoursRow) this.namePill(hoursRow, '');
          this.namePill(pillHost, pillLabel);
        } else {
          this.namePill(nameLine, pillLabel);
        }
      }

      /* ---------------- BOPIS (checklist 9 / #38, 2026-10-23): participation, header pill, short + weekly hours ---------------- */

      safeText(text) {
        return text && !/^Translation missing/i.test(String(text)) ? String(text) : '';
      }

      // "pickup" / "local" / "pickup local" when that service is enabled and not paused today, else ''
      participation(store) {
        if (!store) return '';
        const parts = [];
        if (store.pickup === true && !this.pausedToday(store, 'pickup')) parts.push('pickup');
        if (store.local === true && !this.pausedToday(store, 'local')) parts.push('local');
        return parts.join(' ');
      }

      // the header summary's inline pill on the name line (6.1), created only when there is a
      // label — no markup at all for a non-participating store (6.2)
      namePill(nameLine, label) {
        if (!nameLine) return;
        let pill = nameLine.querySelector('[data-chip-name-pill]');
        if (!label) { if (pill) pill.remove(); return; }
        if (!pill) {
          pill = document.createElement('span');
          pill.className = 'paex-store-chip__name-pill';
          pill.setAttribute('data-chip-name-pill', '');
          nameLine.appendChild(pill);
        }
        if (pill.textContent !== label) pill.textContent = label;
      }

      // "Open · 9 p.m." — paex.fulfil.chip_open_short with the shared 12-hour clock; '' without the API
      shortOpenLine(closeHour) {
        const api = window.paexFulfil;
        if (!(api && typeof api.t === 'function' && typeof api.fmtTime === 'function') || closeHour == null) return '';
        const parts = String(closeHour).split(':');
        const hour = parseInt(parts[0], 10);
        if (!isFinite(hour)) return '';
        try {
          const close = this.safeText(api.fmtTime(hour, parseInt(parts[1] || '0', 10) || 0));
          const text = close ? this.safeText(api.t('chip_open_short', { close })) : '';
          return text && text.indexOf('{') === -1 ? text : '';
        } catch (error) { return ''; }
      }

      // Flyout (#38): the weekly hours built from the store's hours JSON through the shared
      // formatter — "Mon–Fri 10 a.m. – 9 p.m. · Sat 9 a.m. – 7 p.m. · Sun 10 a.m. – 5 p.m."
      // (FR « lun.–ven. 10 h – 21 h · … »); consecutive days with the same hours are grouped,
      // closed days skipped; day names from Intl in the page locale. '' without the API.
      weeklyHours(store) {
        const api = window.paexFulfil;
        const hours = store && store.hours;
        if (!hours || !(api && typeof api.t === 'function' && typeof api.fmtTime === 'function') || typeof paexStoreParseMinutes !== 'function') return '';
        const range = this.safeText(api.t('hours_range'));
        const span = this.safeText(api.t('hours_span'));
        if (!range || !span) return '';
        const raw = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
        const locale = raw.indexOf('-') > -1 ? raw : raw + '-CA';
        let names;
        try { names = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }); } catch (error) { names = null; }
        const dayName = (d) => (names ? names.format(new Date(Date.UTC(2023, 0, 1 + d))) : String(d)); // 2023-01-01 is a Sunday
        const label = (m) => this.safeText(api.fmtTime(Math.floor(m / 60), m % 60));
        const order = [1, 2, 3, 4, 5, 6, 0];
        const groups = [];
        order.forEach((d, index) => {
          const slot = hours[String(d)];
          const open = slot ? paexStoreParseMinutes(slot.open) : null;
          const close = slot ? paexStoreParseMinutes(slot.close) : null;
          if (open == null || close == null || close <= open) return;
          const key = open + '-' + close;
          const last = groups[groups.length - 1];
          if (last && last.key === key && last.index === index - 1) { last.to = d; last.index = index; return; }
          groups.push({ key, from: d, to: d, index, open, close });
        });
        const lines = groups.map((g) => {
          const days = g.from === g.to ? dayName(g.from) : this.safeText(api.t('hours_span', { from: dayName(g.from), to: dayName(g.to) }));
          const open = label(g.open);
          const close = label(g.close);
          return days && open && close ? this.safeText(api.t('hours_range', { days, open, close })) : '';
        }).filter(Boolean);
        return lines.join(' · ');
      }

      /* ---------------- BOPIS (2026-10-22): 12-hour labels + "Pickup available" pills ---------------- */

      // plan 6b-3: EN hour labels switch to 12-hour ("9 p.m.") through the shared
      // formatter window.paexFulfil.fmtTime (paex-fulfil.js, Lane B). Applied ONLY
      // when the template carries the legacy "{close}:00" suffix (the EN header
      // settings); FR templates ("{close} h", Translate & Adapt) and a page without
      // paexFulfil render exactly as today. value = paexStoreTimeLabel (9 | "9:30").
      hourLabel(template, key, value) {
        const api = window.paexFulfil;
        const tpl = String(template == null ? '' : template);
        const token = '{' + key + '}:00';
        const values = {};
        if (api && typeof api.fmtTime === 'function' && value != null && tpl.indexOf(token) !== -1) {
          const parts = String(value).split(':');
          const hour = parseInt(parts[0], 10);
          const minute = parseInt(parts[1] || '0', 10) || 0;
          let label = '';
          if (isFinite(hour)) {
            try { label = api.fmtTime(hour, minute); } catch (error) { label = ''; }
          }
          if (label && label.indexOf('Translation missing') === -1) {
            values[key] = label;
            return paexDeliveryFormat(tpl.split(token).join('{' + key + '}'), values);
          }
        }
        values[key] = value;
        return paexDeliveryFormat(tpl, values);
      }

      // Panel status line = paexStoreHoursText (core), branch for branch, with the
      // 12-hour hour label; without the formatter the core function answers as today.
      hoursStatus(store, texts) {
        const api = window.paexFulfil;
        if (!(api && typeof api.fmtTime === 'function')) return paexStoreHoursText(store, texts);
        const info = paexStoreHoursInfo(store);
        if (!info || !texts) return '';
        if (info.state === 'open' && texts.openNow && texts.closesAt) {
          return texts.openNow + ' · ' + this.hourLabel(texts.closesAt, 'close', info.closeHour);
        }
        if (info.state === 'closes_soon' && texts.closesSoon) {
          return this.hourLabel(texts.closesSoon, 'close', info.closeHour);
        }
        if (info.nextOpenHour != null) {
          if (info.nextOpenIsTomorrow && texts.opensTomorrow) {
            return this.hourLabel(texts.opensTomorrow, 'open', info.nextOpenHour);
          }
          if (texts.opensAt) return this.hourLabel(texts.opensAt, 'open', info.nextOpenHour);
        }
        return '';
      }

      // seams §3.2 pausedToday: paexFulfil when present, else the same rule locally
      // (pause toggle OR today's date in the store tz within the blackout list)
      pausedToday(store, service) {
        if (!store) return true;
        const api = window.paexFulfil;
        if (api && typeof api.pausedToday === 'function') {
          try { return Boolean(api.pausedToday(store, service)); } catch (error) { /* local rule below */ }
        }
        if (service === 'pickup' ? store.pickupPaused === true : store.localPaused === true) return true;
        const dates = Array.isArray(store.blackoutDates) ? store.blackoutDates : [];
        if (!dates.length || typeof paexStoreNowParts !== 'function') return false;
        const today = paexStoreNowParts(new Date(), this.data.tz || 'America/Toronto').dateKey;
        return Boolean(today) && dates.indexOf(today) !== -1;
      }

      // "Pickup available" / "Same-day delivery" pills (paex.fulfil.chip_pickup /
      // chip_local) for a participating store; hidden while that service is paused
      // or today is a blackout day, and when the store is not in the program.
      fulfilPills(target, store) {
        if (!target) return;
        const texts = this.data.texts || {};
        const safe = (text) => (text && !/^Translation missing/i.test(text) ? text : '');
        const labels = [];
        if (store && store.pickup === true && !this.pausedToday(store, 'pickup') && safe(texts.chipPickup)) labels.push(texts.chipPickup);
        if (store && store.local === true && !this.pausedToday(store, 'local') && safe(texts.chipLocal)) labels.push(texts.chipLocal);
        target.textContent = '';
        labels.forEach((label) => {
          const pill = document.createElement('span');
          pill.className = 'paex-store-chip__pillbadge paex-store-chip__pillbadge--now paex-store-chip__pillbadge--pickup';
          pill.textContent = label;
          target.appendChild(pill);
        });
        target.hidden = labels.length === 0;
      }

      renderPanel() {
        const texts = this.data.texts;
        const store =
          this.storeId && paexDeliveryHasStore(this.data.stores, this.storeId)
            ? this.data.stores[this.storeId]
            : null;
        if (!store) return;

        // the compact/minimum chip forms carry no name — the dropdown does
        const panelName = this.querySelector('[data-panel-name]');
        if (panelName) {
          const panelNameText = this.displayNames[this.storeId] || '';
          if (panelName.textContent !== panelNameText) panelName.textContent = panelNameText;
        }
        const address = this.querySelector('[data-panel-address]');
        if (address) {
          const line2 = [store.city, store.province].filter(Boolean).join(', ');
          address.textContent = [store.address1, line2 + (store.zip ? ` ${store.zip}` : '')]
            .filter(Boolean)
            .join('\n');
        }
        // #38: a participating store's panel shows the WEEKLY hours (the chip row above already
        // says "Opens 10 a.m. tomorrow" while the panel is open); otherwise today's status line
        const status = this.querySelector('[data-panel-status]');
        if (status) {
          const weekly = this.hasAttribute('data-fulfil-store') ? this.weeklyHours(store) : '';
          status.textContent = weekly || this.hoursStatus(store, texts);
          status.classList.toggle('paex-store-chip__weekly', Boolean(weekly));
        }
        // BOPIS: participating-store pills (both contexts; the menu accordion is this panel)
        this.fulfilPills(this.querySelector('[data-panel-fulfil]'), store);
        // popup badge + date chips mirror (merchant override 2026-08-03,
        // fresh design: "the dropdown carries the closing date" — even
        // outside the urgency window, so a saved popup never looks
        // permanent). Hidden when the store carries no type/date data.
        const typeDates = this.querySelector('[data-panel-typedates]');
        if (typeDates) {
          typeDates.hidden = !paexStoreFillChips(typeDates, store, 'paex-store-chip__', {
            alwaysClosing: true,
          });
        }
        const phone = this.querySelector('[data-panel-phone]');
        if (phone) {
          if (store.phone) {
            phone.hidden = false;
            phone.href = `tel:${String(store.phone).replace(/[^+\d]/g, '')}`;
            phone.textContent = store.phone;
          } else {
            phone.hidden = true;
          }
        }
        const directions = this.querySelector('[data-panel-directions]');
        if (directions) directions.href = paexDeliveryDirectionsUrl(store);
        const page = this.querySelector('[data-panel-page]');
        if (page) {
          const safePage = paexSafeUrl(store.page);
          if (safePage) {
            page.hidden = false;
            page.href = safePage;
          } else {
            page.hidden = true;
          }
        }
      }

      // The mobile menu flyout auto-closes on focus loss and shares a BOOLEAN
      // scroll lock with modal-dialog (not a counter): opening the store drawer
      // while the menu is open ends with the lock released under the drawer.
      // So: close the menu first, give its toggle handler a tick to release the
      // lock, then open the drawer cleanly.
      openDrawer() {
        const drawer = document.getElementById(this.dataset.drawerId);
        if (!drawer || typeof drawer.show !== 'function') return;
        const button = this.querySelector('[data-chip-summary]');
        const headerDrawer = this.closest('header-drawer');
        const openMenu = headerDrawer ? headerDrawer.querySelector('details.menu-drawer-details[open]') : null;
        if (openMenu) {
          openMenu.removeAttribute('open');
          // the chip goes display:none with the closed menu — restore focus to
          // a visible element (the menu toggle) when the drawer later closes
          const fallback = headerDrawer.querySelector('summary.menu-summary') || button;
          setTimeout(() => drawer.show(fallback), 60);
        } else {
          drawer.show(button);
        }
      }
    }
  );
}
